import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Eye, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { deleteTimesheet, searchTimesheets, type TimesheetItem } from '../api/timesheetService'

type LayoutState = {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
    location?: string
  }
  menus?: any[]
  roles?: any[]
  tenantName?: string
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50']

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const normalizeDateInput = (value: string) => {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function TimesheetListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LayoutState | undefined) ?? {}

  const [permitNo, setPermitNo] = useState('')
  const [stDateTime, setStDateTime] = useState('')
  const [expDateTime, setExpDateTime] = useState('')
  const [items, setItems] = useState<TimesheetItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPage, setTotalPage] = useState(1)
  const [totalRecord, setTotalRecord] = useState(0)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [navigate, state.user])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await searchTimesheets({
        permitNo: permitNo.trim() || null,
        stDateTime: normalizeDateInput(stDateTime),
        expDateTime: normalizeDateInput(expDateTime),
        pageIndex: currentPage,
        pageSize,
      })
      setItems(result.items)
      setCurrentPage(result.currentPage)
      setTotalPage(Math.max(1, result.totalPage))
      setTotalRecord(result.totalRecord)
    } catch (err: any) {
      setError(err?.message ?? 'Unable to load timesheet list.')
      setItems([])
      setTotalPage(1)
      setTotalRecord(0)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, expDateTime, pageSize, permitNo, stDateTime])

  useEffect(() => {
    void loadData()
  }, [loadData, refreshToken])

  const handleSearch = () => {
    setCurrentPage(1)
    setRefreshToken((value) => value + 1)
  }

  const handleClear = () => {
    setPermitNo('')
    setStDateTime('')
    setExpDateTime('')
    setCurrentPage(1)
    setPageSize(10)
    setRefreshToken((value) => value + 1)
  }

  const handleCreate = () => {
    navigate('/timesheet/create', { state })
  }

  const handleEdit = (item: TimesheetItem) => {
    navigate(`/timesheet/${encodeURIComponent(item.permitId)}`, { state })
  }

  const handleView = (item: TimesheetItem) => {
    navigate(`/timesheet/${encodeURIComponent(item.permitId)}/view`, { state })
  }

  const handleDelete = async (item: TimesheetItem) => {
    const confirmed = window.confirm(`Delete timesheet for permit ${item.permitNo}?`)
    if (!confirmed) return

    try {
      await deleteTimesheet(item.permitId)
      setRefreshToken((value) => value + 1)
    } catch (err: any) {
      setError(err?.message ?? 'Unable to delete timesheet.')
    }
  }

  const startIndex = useMemo(() => {
    if (totalRecord === 0) return 0
    return (currentPage - 1) * pageSize + 1
  }, [currentPage, pageSize, totalRecord])

  const endIndex = useMemo(() => {
    if (totalRecord === 0) return 0
    return Math.min(currentPage * pageSize, totalRecord)
  }, [currentPage, pageSize, totalRecord])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <PageTitle
            currentPage="timesheet"
            compact
            titleOverride="Permit Timesheet"
            descriptionOverride="Search, review, create, and maintain permit timesheet records inside the main platform."
          />
        </div>
        <Button onClick={handleCreate} className="self-start bg-emerald-600 hover:bg-emerald-700">
          <Plus className="mr-2 h-4 w-4" />
          Create Timesheet
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search Criteria</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Label htmlFor="permitNo">Permit No</Label>
              <Input
                id="permitNo"
                value={permitNo}
                onChange={(event) => setPermitNo(event.target.value)}
                placeholder="Search by permit number"
                className="mt-1"
              />
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="stDateTime">Working Date From</Label>
              <DatePicker
                id="stDateTime"
                value={stDateTime}
                onChange={setStDateTime}
                allowPastDates
                className="mt-1 h-10"
                placeholder="Select date..."
              />
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="expDateTime">Working Date To</Label>
              <DatePicker
                id="expDateTime"
                value={expDateTime}
                onChange={setExpDateTime}
                allowPastDates
                className="mt-1 h-10"
                placeholder="Select date..."
              />
            </div>

            <div className="flex items-end gap-2 lg:col-span-2 lg:justify-end">
              <Button variant="outline" onClick={handleClear}>
                Refresh
              </Button>
              <Button onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Timesheet Records</CardTitle>
          <Button variant="outline" onClick={() => setRefreshToken((value) => value + 1)} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Permit No</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Plant / Unit</TableHead>
                <TableHead>Working Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Morning</TableHead>
                <TableHead>Afternoon</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                    No timesheet records found.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.permitId}>
                    <TableCell className="font-medium">{item.permitNo}</TableCell>
                    <TableCell>{item.workDescr ?? '-'}</TableCell>
                    <TableCell>{item.companyName ?? '-'}</TableCell>
                    <TableCell>{[item.plant, item.unit].filter(Boolean).join(' : ') || '-'}</TableCell>
                    <TableCell>
                      {formatDate(item.stDateTime)} - {formatDate(item.expDateTime)}
                    </TableCell>
                    <TableCell>{item.permitStatusD ?? '-'}</TableCell>
                    <TableCell>
                      {(item.time1Value ?? '-') + ' - ' + (item.time2Value ?? '-')}
                    </TableCell>
                    <TableCell>
                      {(item.time4Value ?? '-') + ' - ' + (item.time5Value ?? '-')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleView(item)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={() => handleEdit(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(item)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex flex-col gap-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <div>
              Showing {startIndex} - {endIndex} of {totalRecord} records
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span>Page size</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  <SelectTrigger className="h-9 w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((value) => value - 1)}>
                  Prev
                </Button>
                <span>
                  Page {currentPage} / {Math.max(1, totalPage)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.max(1, totalPage)}
                  onClick={() => setCurrentPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
